<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-transit-connection-variant me-2"></i>{{ $t('page.digital_health', 'Digital Health Integrations') }}</h4>
        </div>
        <button class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
      </div>

      <!-- HOW SYNC WORKS — overview panel -->
      <div class="hms-help-panel mb-3">
        <div class="hms-help-head" @click="helpOpen = !helpOpen">
          <i class="mdi mdi-help-circle-outline me-2"></i>
          <strong>How the sync system works</strong>
          <span class="hms-muted-sm ms-2">(click to {{ helpOpen ? 'hide' : 'show' }})</span>
          <i class="mdi ms-auto" :class="helpOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"></i>
        </div>
        <div class="hms-help-body" v-if="helpOpen">
          <p class="mb-2">
            This module <strong>connects your facility to national health systems</strong>. Data flows in two directions:
            you <em>push</em> aggregated reports out to national reporting platforms (DHIS2, eCHIS), and you <em>pull</em>
            reference data in (KMHFL facility list, OpenHIE shared health record).
          </p>
          <ol class="mb-2 ps-3">
            <li><strong>Configure once</strong> — go to the <a href="javascript:void(0)" @click="tab='config'">Configuration</a> tab, enter the endpoint URL, credentials, and toggle <em>Active</em>. Ask your national/county M&amp;E focal person for these details.</li>
            <li><strong>Try Simulate first</strong> — every push has a <em>Simulate</em> button that runs the whole flow but doesn't send data. Use it to check the payload is correct before pushing to production.</li>
            <li><strong>Push when ready</strong> — <em>Push</em> or <em>Sync now</em> sends the real data. The system records who did it and when in the <a href="javascript:void(0)" @click="tab='logs'; loadLogs()">Activity Log</a>.</li>
            <li><strong>Who does this?</strong> Typically the <strong>Health Records Officer</strong> at the end of the reporting period (weekly for community/eCHIS, monthly for DHIS2). Facility admin approves the config once, then it's routine.</li>
          </ol>
          <p class="hms-muted-sm mb-0"><i class="mdi mdi-shield-check me-1"></i>All syncs are audited. Failed pushes retry automatically and the M&amp;E officer is notified on the 3rd failure.</p>
        </div>
      </div>

      <!-- HERO -->
      <div class="hms-hero mb-3">
        <div class="hms-hero-stat" v-for="c in configs" :key="c.config_id" :class="'sys-' + c.system">
          <div class="hms-hero-icon"><i :class="systemIcon(c.system)"></i></div>
          <div class="hms-hero-body">
            <div class="hms-hero-name">{{ systemLabel(c.system) }}</div>
            <div class="hms-hero-status">
              <span v-if="c.is_active == 1" class="hms-pill hms-pill-success">Active</span>
              <span v-else class="hms-pill hms-pill-muted">Inactive</span>
              <span class="hms-muted-sm">{{ c.environment }}</span>
            </div>
            <div class="hms-muted-sm" v-if="c.last_check_at">Last: {{ formatRelative(c.last_check_at) }} · {{ c.last_check_status }}</div>
            <div class="hms-muted-sm" v-else>Never run</div>
          </div>
        </div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='dhis2' }" @click="tab='dhis2'"><i class="mdi mdi-cloud-upload me-1"></i>DHIS2 Push</button>
        <button class="hms-tab" :class="{ active: tab==='kmhfl' }" @click="tab='kmhfl'"><i class="mdi mdi-cloud-download me-1"></i>KMHFL Sync</button>
        <button class="hms-tab" :class="{ active: tab==='echis' }" @click="tab='echis'; loadEchisQueue()"><i class="mdi mdi-account-heart-outline me-1"></i>eCHIS Sync</button>
        <button class="hms-tab" :class="{ active: tab==='config' }" @click="tab='config'"><i class="mdi mdi-cog me-1"></i>Configuration</button>
        <button class="hms-tab" :class="{ active: tab==='logs' }" @click="tab='logs'; loadLogs()"><i class="mdi mdi-history me-1"></i>Activity Log</button>
      </div>

      <!-- ============ DHIS2 PUSH ============ -->
      <div v-if="tab === 'dhis2'">
        <div class="hms-explain mb-3">
          <div class="hms-explain-head"><i class="mdi mdi-cloud-upload me-1"></i>What DHIS2 Push does</div>
          <div class="hms-explain-body">
            DHIS2 is Kenya's <strong>national aggregate health reporting warehouse</strong> (managed by MoH).
            Every month, facilities submit KHIS 105 / MOH forms as aggregate counts (e.g. "234 OPD visits this month").
            <br><br>
            <strong>How to use:</strong>
            <ol class="ps-3 mb-1">
              <li>Pick a <em>Dataset</em> (which MOH form) and <em>Period</em> (usually last month, by the 15th).</li>
              <li>Click <em>Preview</em> — the system computes the numbers from your patient records and shows what will be sent.</li>
              <li>Click <em>Simulate</em> to test the connection without submitting.</li>
              <li>When happy, click <em>Push</em>. DHIS2 replies with a receipt (HTTP 200 = accepted).</li>
            </ol>
            <div class="hms-muted-sm mt-1"><strong>Who:</strong> Health Records Officer, once per reporting period. <strong>Prereq:</strong> Configuration tab must have your DHIS2 URL + username/password + org unit ID from your county.</div>
          </div>
        </div>
        <div class="hms-card mb-3">
          <div class="hms-card-head">Push aggregate dataset to DHIS2</div>
          <div class="hms-card-body">
            <div class="row g-2">
              <div class="col-md-5">
                <label class="hms-label">Dataset</label>
                <select v-model="dhis2.dataset_code" class="hms-input">
                  <option v-for="d in datasets" :key="d.code" :value="d.code">{{ d.name }}</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="hms-label">Period</label>
                <select v-model="dhis2.period" class="hms-input">
                  <option value="this_month">This month</option>
                  <option value="last_month">Last month</option>
                  <option value="this_quarter">This quarter</option>
                  <option value="this_year">This year</option>
                </select>
              </div>
              <div class="col-md-4 d-flex align-items-end gap-2">
                <button class="hms-btn hms-btn-secondary" :disabled="busy" @click="previewPayload"><i class="mdi mdi-eye"></i> Preview</button>
                <button class="hms-btn hms-btn-blue"      :disabled="busy" @click="pushPayload(true)"><i class="mdi mdi-test-tube"></i> Simulate</button>
                <button class="hms-btn hms-btn-success"   :disabled="busy" @click="pushPayload(false)"><i class="mdi mdi-cloud-upload"></i> Push</button>
              </div>
            </div>

            <div v-if="dhis2.lastResult" class="hms-result-banner mt-3" :class="'res-' + dhis2.lastResult.status">
              <div>
                <strong>{{ formatStatus(dhis2.lastResult.status) }}</strong>
                <span v-if="dhis2.lastResult.http_code"> · HTTP {{ dhis2.lastResult.http_code }}</span>
                · {{ dhis2.lastResult.records }} data values
                · {{ dhis2.lastResult.duration_ms }}ms
              </div>
              <button class="hms-btn-link" @click="dhis2.lastResult = null">×</button>
            </div>

            <div v-if="dhis2.previewPayload" class="mt-3">
              <div class="hms-section-label d-flex justify-content-between">
                <span>FHIR-style dataValueSet payload preview</span>
                <span class="hms-muted-sm" v-if="dhis2.endpointBase">→ {{ dhis2.endpointBase }}/dataValueSets</span>
              </div>
              <pre class="hms-json">{{ JSON.stringify(dhis2.previewPayload, null, 2) }}</pre>
            </div>
          </div>
        </div>

        <div class="hms-card">
          <div class="hms-card-head">Computed values for {{ formatDataset(dhis2.dataset_code) }}</div>
          <div class="hms-card-body p-0">
            <table class="hms-table" v-if="dhis2.previewPayload && dhis2.previewPayload.dataValues.length">
              <thead><tr><th>Metric</th><th>DHIS2 Data Element</th><th>Value</th></tr></thead>
              <tbody>
                <tr v-for="(v, i) in dhis2.previewPayload.dataValues" :key="i">
                  <td>{{ v.comment || v.dataElement }}</td>
                  <td><code>{{ v.dataElement }}</code></td>
                  <td><strong>{{ v.value }}</strong></td>
                </tr>
              </tbody>
            </table>
            <div v-else class="hms-muted text-center hms-pad">Click <strong>Preview</strong> to compute and inspect the payload.</div>
          </div>
        </div>
      </div>

      <!-- ============ KMHFL SYNC ============ -->
      <div v-if="tab === 'kmhfl'">
        <div class="hms-explain mb-3">
          <div class="hms-explain-head"><i class="mdi mdi-cloud-download me-1"></i>What KMHFL Sync does</div>
          <div class="hms-explain-body">
            KMHFL = <strong>Kenya Master Health Facility List</strong> — the official MoH registry of every hospital, clinic,
            dispensary, and health post in Kenya. Each facility has a unique <em>KMFL code</em>.
            <br><br>
            <strong>Why sync?</strong> When you refer a patient elsewhere, or receive a referral, both facilities need to be
            identified by their official KMFL code (not a free-text name). This sync makes sure your branch list is aligned
            with the national registry.
            <br><br>
            <strong>How to use:</strong>
            <ol class="ps-3 mb-1">
              <li>Click <em>Simulate sync</em> to fetch facilities for your county without changing anything — check that names match.</li>
              <li>Click <em>Sync now</em> to write the KMFL codes into your own branch records.</li>
            </ol>
            <div class="hms-muted-sm mt-1"><strong>Who:</strong> Facility admin, once at setup and then quarterly. <strong>Prereq:</strong> Your branches have real facility names entered.</div>
          </div>
        </div>
        <div class="hms-card mb-3">
          <div class="hms-card-head">Sync facilities from Kenya Master Health Facility List</div>
          <div class="hms-card-body">
            <div class="hms-info-banner">
              <i class="mdi mdi-information"></i>
              Pulls the canonical facility list for county code <strong>{{ countyCode }}</strong>, matches them to your <code>branches</code> by name or KMFL code, and populates missing <code>kmfl_code</code> values.
            </div>
            <div class="d-flex gap-2 mt-2">
              <button class="hms-btn hms-btn-blue"    :disabled="busy" @click="syncKmhfl(true)"><i class="mdi mdi-test-tube"></i> Simulate sync</button>
              <button class="hms-btn hms-btn-success" :disabled="busy" @click="syncKmhfl(false)"><i class="mdi mdi-cloud-download"></i> Sync now</button>
            </div>

            <div v-if="kmhfl.lastResult" class="hms-result-banner mt-3" :class="'res-' + kmhfl.lastResult.status">
              <div>
                <strong>{{ formatStatus(kmhfl.lastResult.status) }}</strong>
                · fetched {{ kmhfl.lastResult.fetched }}
                · matched {{ kmhfl.lastResult.matched }}
                · {{ kmhfl.lastResult.kmfl_codes_applied }} KMFL codes updated
                · {{ kmhfl.lastResult.duration_ms }}ms
              </div>
              <button class="hms-btn-link" @click="kmhfl.lastResult = null">×</button>
            </div>
          </div>
        </div>

        <div v-if="kmhfl.lastResult && kmhfl.lastResult.facilities" class="hms-card">
          <div class="hms-card-head">Returned facilities ({{ kmhfl.lastResult.facilities.length }})</div>
          <div class="hms-card-body p-0">
            <table class="hms-table">
              <thead><tr><th>KMFL Code</th><th>Facility</th><th>KEPH Level</th><th>Sub-county</th></tr></thead>
              <tbody>
                <tr v-for="(f, i) in kmhfl.lastResult.facilities" :key="i">
                  <td><strong>{{ f.code }}</strong></td>
                  <td>{{ f.name }}</td>
                  <td><span class="hms-pill" :class="kephPill(f.keph_level)">L{{ f.keph_level }}</span></td>
                  <td>{{ f.sub_county || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ============ eCHIS SYNC ============ -->
      <div v-if="tab === 'echis'">
        <div class="hms-explain mb-3">
          <div class="hms-explain-head"><i class="mdi mdi-account-heart-outline me-1"></i>What eCHIS Sync does</div>
          <div class="hms-explain-body">
            eCHIS = <strong>electronic Community Health Information System</strong> — the national platform for community
            health work. It tracks Community Health Units (CHUs), Community Health Promoters (CHPs), the households
            they cover, and every household visit they log.
            <br><br>
            <strong>How to use:</strong> The counters above show what's queued locally (new CHUs, CHPs, households, and
            visits since your last successful sync).
            <ol class="ps-3 mb-1">
              <li>Click <em>Simulate</em> to see what will be pushed without submitting.</li>
              <li>Click <em>Push to eCHIS</em> to send everything to the national platform. It goes as an incremental delta — only new/changed records.</li>
              <li>Successful sync marks records as "synced" so they don't get re-sent.</li>
            </ol>
            <div class="hms-muted-sm mt-1"><strong>Who:</strong> Community Health Assistant or M&amp;E officer, typically weekly or after each CHP field day. <strong>Prereq:</strong> eCHIS credentials configured; CHUs/CHPs registered in Community Health module.</div>
          </div>
        </div>
        <div class="hms-card mb-3">
          <div class="hms-card-head">Push community-health data to eCHIS</div>
          <div class="hms-card-body">
            <div class="hms-info-banner">
              <i class="mdi mdi-information"></i>
              Pushes incremental changes (CHUs, CHPs, households, visits) since the last successful sync to the national eCHIS platform.
              Last sync: <strong>{{ echis.lastSyncAt || 'never' }}</strong>
            </div>
            <div class="hms-grid-3 mt-3">
              <div class="hms-kpi-mini"><div class="hms-mini-num">{{ echis.queue.units_pending || 0 }}</div><div class="hms-mini-lbl">CHUs pending</div></div>
              <div class="hms-kpi-mini"><div class="hms-mini-num">{{ echis.queue.chps_pending || 0 }}</div><div class="hms-mini-lbl">CHPs pending</div></div>
              <div class="hms-kpi-mini"><div class="hms-mini-num">{{ echis.queue.households_pending || 0 }}</div><div class="hms-mini-lbl">Households pending</div></div>
              <div class="hms-kpi-mini"><div class="hms-mini-num">{{ echis.queue.visits_pending || 0 }}</div><div class="hms-mini-lbl">Visits pending</div></div>
            </div>
            <div class="d-flex gap-2 mt-3">
              <button class="hms-btn hms-btn-blue" :disabled="busy" @click="doEchisPush(true)"><i class="mdi mdi-test-tube"></i> Simulate</button>
              <button class="hms-btn hms-btn-success" :disabled="busy" @click="doEchisPush(false)"><i class="mdi mdi-cloud-upload"></i> Push to eCHIS</button>
            </div>
            <div v-if="echis.lastResult" class="hms-result-banner mt-3" :class="'res-' + echis.lastResult.status">
              <div>
                <strong>{{ formatStatus(echis.lastResult.status) }}</strong>
                <span v-if="echis.lastResult.http_code"> · HTTP {{ echis.lastResult.http_code }}</span>
                · {{ echis.lastResult.records }} records
                · {{ echis.lastResult.duration_ms }}ms
              </div>
              <div v-if="echis.lastResult.breakdown" class="hms-muted-sm">
                CHUs {{ echis.lastResult.breakdown.units }} · CHPs {{ echis.lastResult.breakdown.chps }} · Households {{ echis.lastResult.breakdown.households }} · Visits {{ echis.lastResult.breakdown.visits }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ CONFIGURATION ============ -->
      <div v-if="tab === 'config'">
        <div class="hms-card" v-for="c in configs" :key="c.config_id" :class="'sys-card-' + c.system">
          <div class="hms-card-head d-flex justify-content-between">
            <span><i :class="systemIcon(c.system)"></i> {{ systemLabel(c.system) }}</span>
            <span class="hms-muted-sm">{{ c.environment }}</span>
          </div>
          <div class="hms-card-body">
            <div class="row g-2">
              <div class="col-md-3"><label class="hms-label">Environment</label>
                <select v-model="c.environment" class="hms-input">
                  <option value="test">Test</option>
                  <option value="staging">Staging</option>
                  <option value="prod">Production</option>
                </select>
              </div>
              <div class="col-md-7"><label class="hms-label">Base URL</label><input v-model="c.base_url" class="hms-input" /></div>
              <div class="col-md-2 d-flex align-items-end"><label><input type="checkbox" v-model="c.is_active" :true-value="1" :false-value="0" /> Active</label></div>
              <div class="col-md-3"><label class="hms-label">Username</label><input v-model="c.username" class="hms-input" /></div>
              <div class="col-md-4"><label class="hms-label">Password / API key</label><input v-model="c.password_or_key" type="password" class="hms-input" placeholder="leave as ********** to keep current" /></div>
              <div class="col-md-3"><label class="hms-label">Org unit ID</label><input v-model="c.org_unit_id" class="hms-input" /></div>
              <div class="col-md-2"><label class="hms-label">Org unit code</label><input v-model="c.org_unit_code" class="hms-input" /></div>
              <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="c.notes" class="hms-input" rows="2"></textarea></div>
            </div>
            <div class="d-flex justify-content-end mt-2">
              <button class="hms-btn hms-btn-success" :disabled="busy" @click="saveConfig(c)"><i class="mdi mdi-content-save"></i> Save</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ LOGS ============ -->
      <div v-if="tab === 'logs'">
        <div class="d-flex gap-2 mb-2">
          <select v-model="logFilter" class="hms-input" @change="loadLogs" style="max-width:200px">
            <option value="">All systems</option>
            <option value="dhis2">DHIS2</option>
            <option value="kmhfl">KMHFL</option>
            <option value="echis">eCHIS</option>
          </select>
        </div>
        <table class="hms-table">
          <thead><tr><th>When</th><th>System</th><th>Action</th><th>Dataset / Period</th><th>Status</th><th>HTTP</th><th>Records</th><th>Duration</th><th>By</th></tr></thead>
          <tbody>
            <tr v-for="l in logs" :key="l.log_id" :class="logRowClass(l.status)">
              <td>{{ formatDateTime(l.created_at) }}</td>
              <td><span class="hms-pill hms-pill-info">{{ l.system }}</span></td>
              <td>{{ l.action }}</td>
              <td>{{ l.dataset_code || '—' }}<span v-if="l.period"> · {{ l.period }}</span></td>
              <td><span class="hms-pill" :class="logStatusPill(l.status)">{{ l.status }}</span></td>
              <td>{{ l.http_code || '—' }}</td>
              <td>{{ l.records_count || 0 }}</td>
              <td>{{ l.duration_ms || 0 }}ms</td>
              <td>{{ l.triggered_by_name || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "DigitalHealth",
  components: { Navigation },
  data() {
    return {
      tab: 'dhis2', busy: false, helpOpen: false,
      configs: [], logs: [], logFilter: '',

      datasets: [
        { code: 'moh731_art', name: 'MoH 731 — HIV / ART (monthly)' },
        { code: 'moh711_tb',  name: 'MoH 711 — TB (monthly)' },
        { code: 'kepi',       name: 'KEPI — Immunization (monthly)' },
        { code: 'moh705',     name: 'MoH 705 — OPD (monthly)' }
      ],

      dhis2: { dataset_code: 'moh731_art', period: 'this_month', previewPayload: null, endpointBase: null, lastResult: null },
      kmhfl: { lastResult: null },
      echis: { queue: {}, lastSyncAt: null, lastResult: null }
    };
  },
  computed: {
    countyCode() {
      const c = this.configs.find(x => x.system === 'kmhfl');
      return c ? (c.org_unit_code || '033') : '033';
    }
  },
  mounted() { this.loadAll(); },
  methods: {
    async loadAll() {
      try {
        const r = await executeJson({ function: 'getDigitalHealthConfig' }, hms);
        if (r.data && r.data.success) this.configs = (r.data.data && r.data.data.Configs) || [];
      } catch (e) { console.error(e); }
    },
    async loadLogs() {
      try {
        const r = await executeJson({ function: 'getDigitalHealthLogs', system: this.logFilter }, hms);
        if (r.data && r.data.success) this.logs = (r.data.data && r.data.data.Logs) || [];
      } catch (e) { console.error(e); }
    },

    async previewPayload() {
      this.busy = true;
      try {
        const r = await executeJson({ function: 'previewDhis2Payload', dataset_code: this.dhis2.dataset_code, period: this.dhis2.period }, hms);
        if (r.data && r.data.success) {
          this.dhis2.previewPayload = (r.data.data && r.data.data.Payload) || null;
          this.dhis2.endpointBase   = r.data.data && r.data.data.EndpointBase;
        }
      } catch (e) { console.error(e); }
      finally { this.busy = false; }
    },
    async pushPayload(simulate) {
      this.busy = true;
      try {
        const r = await executeJson({ function: 'pushToDhis2', dataset_code: this.dhis2.dataset_code, period: this.dhis2.period, simulate: simulate ? 1 : 0 }, hms);
        if (r.data && r.data.success) {
          this.dhis2.lastResult = r.data.data;
          this.dhis2.previewPayload = r.data.data.payload;
          await this.loadAll();
        } else {
          alert((r.data && r.data.message) || 'Push failed.');
        }
      } catch (e) { console.error(e); }
      finally { this.busy = false; }
    },

    async loadEchisQueue() {
      try {
        const r = await executeJson({ function: 'getEchisQueue' }, hms);
        if (r.data && r.data.success) {
          this.echis.queue = (r.data.data && r.data.data.Queue) || {};
          this.echis.lastSyncAt = r.data.data && r.data.data.LastSyncAt;
        }
      } catch (e) { console.error(e); }
    },
    async doEchisPush(simulate) {
      this.busy = true;
      try {
        const r = await executeJson({ function: 'pushToEchis', simulate: simulate ? 1 : 0 }, hms);
        if (r.data && r.data.success) {
          this.echis.lastResult = r.data.data;
          await this.loadEchisQueue();
          await this.loadAll();
        } else {
          alert((r.data && r.data.message) || 'Push failed.');
        }
      } catch (e) { console.error(e); }
      finally { this.busy = false; }
    },

    async syncKmhfl(simulate) {
      this.busy = true;
      try {
        const r = await executeJson({ function: 'syncFromKmhfl', simulate: simulate ? 1 : 0 }, hms);
        if (r.data && r.data.success) {
          this.kmhfl.lastResult = r.data.data;
          await this.loadAll();
        } else {
          alert((r.data && r.data.message) || 'Sync failed.');
        }
      } catch (e) { console.error(e); }
      finally { this.busy = false; }
    },

    async saveConfig(c) {
      this.busy = true;
      try {
        const params = {
          function: 'saveDigitalHealthConfig',
          config_id: c.config_id,
          environment: c.environment,
          base_url: c.base_url,
          username: c.username || '',
          org_unit_id: c.org_unit_id || '',
          org_unit_code: c.org_unit_code || '',
          is_active: c.is_active,
          notes: c.notes || ''
        };
        if (c.password_or_key && c.password_or_key !== '************') params.password_or_key = c.password_or_key;
        const r = await executeJson(params, hms);
        if (r.data && r.data.success) await this.loadAll();
        else alert((r.data && r.data.message) || 'Save failed.');
      } catch (e) { console.error(e); }
      finally { this.busy = false; }
    },

    systemIcon(s) { return ({ dhis2: 'mdi mdi-chart-bar-stacked', kmhfl: 'mdi mdi-hospital-building', echis: 'mdi mdi-account-heart-outline', openhie: 'mdi mdi-link-variant' })[s] || 'mdi mdi-link'; },
    systemLabel(s) { return ({ dhis2: 'DHIS2', kmhfl: 'KMHFL (Master Facility List)', echis: 'eCHIS', openhie: 'OpenHIE / SHR' })[s] || s; },
    formatDataset(c) { const d = this.datasets.find(x => x.code === c); return d ? d.name : c; },
    formatStatus(s) { return ({ success: 'Success', simulated: 'Simulated', error: 'Error', partial: 'Partial' })[s] || s; },
    logStatusPill(s) { return ({ success: 'hms-pill-success', simulated: 'hms-pill-info', error: 'hms-pill-danger', partial: 'hms-pill-warn' })[s] || ''; },
    logRowClass(s) { return s === 'error' ? 'row-action' : (s === 'partial' ? 'row-warn' : ''); },
    kephPill(l) { const lv = parseInt(l || 4); if (lv >= 5) return 'hms-pill-danger'; if (lv === 4) return 'hms-pill-warn'; return 'hms-pill-info'; },
    formatRelative(s) {
      if (!s) return '—';
      const d = new Date(s.replace ? s.replace(' ', 'T') : s);
      if (isNaN(d)) return s;
      const mins = Math.floor((Date.now() - d.getTime()) / 60000);
      if (mins < 60) return mins + 'm ago';
      const h = Math.floor(mins / 60);
      if (h < 24) return h + 'h ago';
      return Math.floor(h / 24) + 'd ago';
    },
    formatDateTime(s) { if (!s) return '—'; const d = new Date(s.replace ? s.replace(' ','T') : s); return isNaN(d) ? s : d.toLocaleString(); }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-hero { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
@media (max-width: 992px) { .hms-hero { grid-template-columns: repeat(2, 1fr); } }
.hms-hero-stat { display: flex; gap: 12px; align-items: center; padding: 14px; border-radius: 10px; color: #fff; }
.hms-hero-stat.sys-dhis2   { background: linear-gradient(120deg, #1e3a8a, #1d4ed8); }
.hms-hero-stat.sys-kmhfl   { background: linear-gradient(120deg, #166534, #16a34a); }
.hms-hero-stat.sys-echis   { background: linear-gradient(120deg, #b45309, #d97706); }
.hms-hero-stat.sys-openhie { background: linear-gradient(120deg, #6b21a8, #9333ea); }
.hms-hero-icon .mdi { font-size: 32px; }
.hms-hero-name { font-size: 16px; font-weight: 700; }
.hms-hero-status { display: flex; gap: 6px; align-items: center; margin-top: 2px; }
.hms-hero-status .hms-muted-sm { color: rgba(255,255,255,.85); }
.hms-hero-stat .hms-muted-sm { color: rgba(255,255,255,.85); }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; margin-bottom: 12px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-head .mdi { color: #2b6cb0; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }

.hms-section-label { font-size: 11px; font-weight: 700; color: #4a5568; letter-spacing: 1px; padding: 4px 0; border-bottom: 1px solid #edf2f7; margin: 8px 0 6px; }

.hms-result-banner { padding: 10px 14px; border-radius: 6px; display: flex; justify-content: space-between; align-items: center; }
.hms-result-banner.res-success   { background: #f0fff4; border: 1px solid #9ae6b4; color: #22543d; }
.hms-result-banner.res-simulated { background: #ebf8ff; border: 1px solid #90cdf4; color: #2c5282; }
.hms-result-banner.res-error     { background: #fff5f5; border: 1px solid #fc8181; color: #742a2a; }
.hms-result-banner.res-partial   { background: #fffaf0; border: 1px solid #f6ad55; color: #7b341e; }

.hms-info-banner { background: #ebf8ff; border: 1px solid #90cdf4; color: #2c5282; padding: 10px 14px; border-radius: 6px; font-size: 13px; }
.hms-info-banner .mdi { margin-right: 6px; }

.hms-json { background: #1a202c; color: #e2e8f0; padding: 14px; border-radius: 6px; font-size: 12px; max-height: 400px; overflow: auto; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr.row-warn   { background: #fffaf0; }
.hms-table tr.row-action { background: #fff5f5; }
.hms-table code { background: #f7fafc; padding: 2px 6px; border-radius: 4px; font-size: 11px; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }
.hms-btn-link      { background: transparent; color: #2b6cb0; border: none; cursor: pointer; }

.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-pad { padding: 24px; }

.hms-grid-3 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
.hms-kpi-mini { background: #f7fafc; border-radius: 6px; padding: 10px; text-align: center; }
.hms-mini-num { font-size: 24px; font-weight: 700; color: #1a202c; }
.hms-mini-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }

/* Help / explainer panels (item 9) */
.hms-help-panel { background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; overflow: hidden; }
.hms-help-head { padding: 10px 14px; display: flex; align-items: center; cursor: pointer; color: #1e40af; font-size: 14px; }
.hms-help-head:hover { background: #dbeafe; }
.hms-help-body { padding: 14px 18px 16px; border-top: 1px solid #bfdbfe; font-size: 13px; color: #1e3a8a; background: #eff6ff; }
.hms-help-body a { color: #1d4ed8; text-decoration: underline; }
.hms-help-body ol { margin: 6px 0; }

.hms-explain { background: #f0fdfa; border-left: 4px solid #14b8a6; border-radius: 6px; padding: 12px 14px; }
.hms-explain-head { font-weight: 700; color: #0f766e; font-size: 13px; margin-bottom: 6px; }
.hms-explain-body { font-size: 13px; color: #134e4a; line-height: 1.55; }
.hms-explain-body em { font-style: normal; background: #ccfbf1; padding: 0 4px; border-radius: 3px; }
</style>
